import React, { useState, useRef, useEffect } from 'react';
import { GameState, ManagerHistoryEntry, ClubHistory, Objective, GameConfig, TeamInLeague } from '../../domain/entities';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { ManagerHelpModal } from '../shared/ManagerHelpModal';
import { TeamBadge } from '../shared/TeamBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase } from '@fortawesome/free-solid-svg-icons';
import './ManagerScreen.css';
import { View } from '../../App';
import { formatValue } from '../../utils/utilities';

const getPositionMedal = (pos: number) => {
    if (pos === 1) return '🥇';
    if (pos === 2) return '🥈';
    if (pos === 3) return '🥉';
    return pos ? pos.toString() : 'N/A';
}

const getPositionDisplay = (record: ManagerHistoryEntry, leagueTeamsCount: number, config: GameConfig): string => {
    const medal = getPositionMedal(record.position);
    if (!record.position) return medal;

    const leagueConfig = config.LEAGUE_CONFIG[record.leagueName];
    if (!leagueConfig) return medal;

    const { promotion, relegation } = leagueConfig;
    const teams = leagueTeamsCount;

    if (promotion > 0 && record.position <= promotion) {
        return `${medal} (Promoted)`;
    }

    if (relegation > 0 && record.position > teams - relegation) {
        return `${medal} (Relegated)`;
    }

    return medal;
};

const BoardView: React.FC<{ team: TeamInLeague }> = ({ team }) => {
    const { boardConfidence, seasonObjectives } = team;
    
    let confidenceText: string;
    let confidenceColor: string;
    if (boardConfidence > 80) {
        confidenceText = 'Delighted';
        confidenceColor = 'var(--positive-color)';
    } else if (boardConfidence > 60) {
        confidenceText = 'Pleased';
        confidenceColor = 'var(--positive-color)';
    } else if (boardConfidence > 40) {
        confidenceText = 'Content';
        confidenceColor = 'var(--info-color)';
    } else if (boardConfidence > 20) {
        confidenceText = 'Concerned';
        confidenceColor = 'var(--negative-color)';
    } else {
        confidenceText = 'Furious';
        confidenceColor = 'var(--negative-color)';
    }
    
    return (
        <div className="panel panel-no-border-margin">
            <div className="panel-header">Board Confidence</div>
            <div className="panel-content">
                <div className="confidence-meter">
                    <div className="confidence-bar-container">
                        <div className="confidence-bar" style={{ width: `${boardConfidence}%`, backgroundColor: confidenceColor }}></div>
                    </div>
                    <div className="confidence-text">
                        <span style={{ color: confidenceColor }}>{confidenceText}</span> ({boardConfidence}%)
                    </div>
                </div>
            </div>
            <div className="panel-header">Season Objectives</div>
            <div className="panel-content">
                <ul className="objectives-list">
                    {seasonObjectives.map((obj, i) => (
                        <li key={i} className={obj.isMet ? 'met' : ''}>
                            <span className="objective-status">{obj.isMet ? '✅' : '⏳'}</span>
                            <div className="objective-text">
                                <span className="objective-description">{obj.description}</span>
                                {obj.isPrimary && <span className="objective-tag">Primary</span>}
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};


const getChallengeLabel = (type: string): string => {
    switch (type) {
        case 'great_escape': return '🚀 Great Escape';
        case 'youth_academy': return '👶 Youth Academy';
        case 'debt_crisis': return '💰 Debt Crisis';
        default: return '';
    }
};

export const SeasonCard: React.FC<{ record: ManagerHistoryEntry, isCurrent: boolean, leagueTeamsCount: number, config: GameConfig, gameState: GameState }> = ({ record, isCurrent, leagueTeamsCount, config, gameState }) => {
    const netSpend = record.income - record.spent;

    const getTeamByName = (name: string) => {
        for (const league of Object.values(gameState.leagues)) {
            const team = league.teams.find(t => t.name === name);
            if (team) return team;
        }
        return undefined;
    };

    return (
        <div className="season-card">
            <div className="season-card-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div>
                        <h3>Season {record.season} {isCurrent && '(Current)'}</h3>
                        <span><span className="player-team-name">{getTeamByName(record.teamName)?.badge && <TeamBadge badge={getTeamByName(record.teamName)?.badge} color={getTeamByName(record.teamName)?.colors.primary} size="sm" />}{record.teamName}</span> - {record.leagueName}</span>
                    </div>
                    {record.challenge && (
                        <div style={{
                            backgroundColor: 'var(--warning-color)',
                            color: 'var(--background-color)',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '0.85em',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap'
                        }}>
                            {getChallengeLabel(record.challenge.type)}
                        </div>
                    )}
                </div>
            </div>
            <ul>
                <li>
                    <span className="label">Position</span>
                    <strong>{getPositionDisplay(record, leagueTeamsCount, config)}</strong>
                </li>
                <li>
                    <span className="label">Record (W-D-L)</span>
                    <strong>{record.wins}-{record.draws}-{record.losses}</strong>
                </li>
                <li>
                    <span className="label">Goals (F-A)</span>
                    <strong>{record.goalsFor}-{record.goalsAgainst}</strong>
                </li>
                <li>
                    <span className="label">Transfer Spend</span>
                    <strong className="negative">{formatValue(record.spent)}</strong>
                </li>
                 <li>
                    <span className="label">Transfer Income</span>
                    <strong className="positive">{formatValue(record.income)}</strong>
                </li>
                <li>
                    <span className="label">Net Spend</span>
                    <strong className={netSpend >= 0 ? 'positive' : 'negative'}>
                        {netSpend >= 0 ? `+${formatValue(netSpend)}` : `-${formatValue(Math.abs(netSpend))}`}
                    </strong>
                </li>
            </ul>
        </div>
    );
};

const ClubRecordsView: React.FC<{ clubHistory: ClubHistory }> = ({ clubHistory }) => {
    return (
        <div className="panel panel-no-border-margin">
            <div className="panel-header">All-Time Club Records</div>
            <div className="grid-layout" style={{ padding: '12px' }}>
                <div className="finance-card">
                    <h3>Most Appearances</h3>
                    <p>👥 {clubHistory.mostAppearances.playerName || 'N/A'}</p>
                    <strong>{clubHistory.mostAppearances.value} games</strong>
                </div>
                <div className="finance-card">
                    <h3>Most Goals</h3>
                    <p>⚽️ {clubHistory.mostGoals.playerName || 'N/A'}</p>
                    <strong>{clubHistory.mostGoals.value} goals</strong>
                </div>
                <div className="finance-card">
                    <h3>Record Signing</h3>
                    <p className="positive">🔼 {clubHistory.recordTransferIn.playerName || 'N/A'}</p>
                    <strong>{formatValue(clubHistory.recordTransferIn.fee || 0)}</strong>
                </div>
                 <div className="finance-card">
                    <h3>Record Sale</h3>
                    <p className="negative">🔽 {clubHistory.recordTransferOut.playerName || 'N/A'}</p>
                    <strong>{formatValue(clubHistory.recordTransferOut.fee || 0)}</strong>
                </div>
            </div>
            <div className="panel-header">Hall of Fame</div>
            <div className="table-wrapper">
                <table className="table">
                    <thead>
                        <tr>
                            <th>Player</th>
                            <th>Appearances</th>
                            <th>Goals</th>
                            <th>Retired</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clubHistory.retiredLegends.length === 0 && (
                            <tr><td colSpan={4} style={{textAlign: 'center'}}>No legends have been inducted yet.</td></tr>
                        )}
                        {[...(clubHistory.retiredLegends || [])].sort((a,b) => b.appearances - a.appearances).map(legend => (
                            <tr key={legend.playerName}>
                                <td>{legend.playerName}</td>
                                <td>{legend.appearances}</td>
                                <td>{legend.goals}</td>
                                <td>Season {legend.retiredSeason}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

interface ManagerScreenProps {
    gameState: GameState;
    playerTeam: TeamInLeague;
    pushView: (view: View) => void;
    adsEnabled: boolean;
    onUpdateManagerName: (name: string) => void;
}

export const ManagerScreen: React.FC<ManagerScreenProps> = ({ gameState, playerTeam, adsEnabled, onUpdateManagerName }) => {
    const [activeTab, setActiveTab] = useState<'career' | 'board' | 'records'>('career');
    const [isSharing, setIsSharing] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [name, setName] = useState(gameState.managerName || 'Manager');
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const nameInputRef = useRef<HTMLInputElement>(null);

    const currentLeague = gameState.leagues[gameState.playerLeagueName];
    const currentPosition = currentLeague.teams.findIndex(t => t.name === playerTeam.name) + 1;

    const seasonTransactions = playerTeam.finances.history.filter(h => h.season === gameState.season);
    const spent = seasonTransactions.filter(h => h.event.startsWith('Bought')).reduce((sum, h) => sum - h.amount, 0);
    const income = seasonTransactions.filter(h => h.event.startsWith('Sold')).reduce((sum, h) => sum + h.amount, 0);

    const currentSeasonEntry: ManagerHistoryEntry = {
        season: gameState.season,
        teamName: playerTeam.name,
        leagueName: currentLeague.name,
        position: currentPosition,
        played: playerTeam.played,
        wins: playerTeam.won,
        draws: playerTeam.drawn,
        losses: playerTeam.lost,
        goalsFor: playerTeam.goalsFor,
        goalsAgainst: playerTeam.goalsAgainst,
        spent,
        income,
    };
    
    const allHistory = [...gameState.managerHistory, currentSeasonEntry];
    const reversedHistory = [...allHistory].reverse();

    useEffect(() => {
        if (isEditingName) {
            nameInputRef.current?.focus();
            nameInputRef.current?.select();
        }
    }, [isEditingName]);

    const handleNameSave = () => {
        if (name.trim()) {
            onUpdateManagerName(name.trim());
        } else {
            setName(gameState.managerName || 'Manager'); // Revert if empty
        }
        setIsEditingName(false);
    };

    const handleShare = async () => {
        setIsSharing(true);
        try {
            await document.fonts.load("12px 'Silkscreen'");

            const FONT = "'Silkscreen', sans-serif";
            const docStyle = getComputedStyle(document.documentElement);
            const BG_COLOR = docStyle.getPropertyValue('--background-color').trim();
            const CARD_BG_COLOR = docStyle.getPropertyValue('--primary-color').trim();
            const TEXT_COLOR = docStyle.getPropertyValue('--text-color').trim();
            const POSITIVE_COLOR = docStyle.getPropertyValue('--positive-color').trim();
            const NEGATIVE_COLOR = docStyle.getPropertyValue('--negative-color').trim();
            const ACCENT_TEXT_COLOR = docStyle.getPropertyValue('--accent-text-color').trim() || '#00ff9b';
            const BORDER_COLOR = docStyle.getPropertyValue('--primary-color').trim();

            const cheatsUsed: string[] = [];
            if (gameState.debugFlags) {
                if (gameState.debugFlags.moneyAdded) cheatsUsed.push('added money');
                if (gameState.debugFlags.adsDisabled) cheatsUsed.push('disabled ads');
                if (gameState.debugFlags.simulationUsed) cheatsUsed.push('used sim tools');
                if (gameState.debugFlags.aiPlayerUsed) cheatsUsed.push('used AI player');
            }

            const historyByTeam = reversedHistory.reduce((acc, record) => {
                if (!acc[record.teamName]) {
                    acc[record.teamName] = [];
                }
                acc[record.teamName].push(record);
                return acc;
            }, {} as Record<string, ManagerHistoryEntry[]>);
            
            const numTeams = Object.keys(historyByTeam).length;
            const numRecords = reversedHistory.length;

            const CANVAS_WIDTH = 800;
            const PADDING = 40;
            const HEADER_HEIGHT = 120;
            const FOOTER_HEIGHT = 60;
            const CARD_HEIGHT = 180;
            const TEAM_HEADER_HEIGHT = 60;
            const GAP = 20;
            const CHEAT_FOOTER_HEIGHT = cheatsUsed.length > 0 ? 30 : 0;

            const CANVAS_HEIGHT = HEADER_HEIGHT + (numTeams * TEAM_HEADER_HEIGHT) + (numRecords * (CARD_HEIGHT + GAP)) - (numRecords > 0 ? GAP : 0) + FOOTER_HEIGHT + CHEAT_FOOTER_HEIGHT;

            const canvas = document.createElement('canvas');
            canvas.width = CANVAS_WIDTH;
            canvas.height = CANVAS_HEIGHT;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            ctx.fillStyle = BG_COLOR;
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            ctx.textAlign = 'center';
            ctx.font = `bold 36px ${FONT}`;
            ctx.fillStyle = TEXT_COLOR;
            ctx.fillText(`${gameState.managerName || 'Manager'}'s CAREER`, CANVAS_WIDTH / 2, PADDING + 30);
            ctx.font = `16px ${FONT}`;
            ctx.fillStyle = ACCENT_TEXT_COLOR;
            ctx.fillText('PIXEL SOCCER MANAGER', CANVAS_WIDTH / 2, PADDING + 70);
            
            let currentY = HEADER_HEIGHT;

            for (const teamName of Object.keys(historyByTeam)) {
                const teamRecords = historyByTeam[teamName];
                
                ctx.font = `bold 24px ${FONT}`;
                ctx.fillStyle = ACCENT_TEXT_COLOR;
                ctx.textAlign = 'left';
                ctx.fillText(teamName, PADDING, currentY + 40);
                currentY += TEAM_HEADER_HEIGHT;

                teamRecords.forEach((record) => {
                    const y = currentY;
                    ctx.fillStyle = CARD_BG_COLOR;
                    ctx.strokeStyle = BORDER_COLOR;
                    ctx.lineWidth = 2;
                    ctx.strokeRect(PADDING, y, CANVAS_WIDTH - PADDING * 2, CARD_HEIGHT);
                    ctx.fillRect(PADDING, y, CANVAS_WIDTH - PADDING * 2, CARD_HEIGHT);

                    ctx.textAlign = 'left';
                    ctx.font = `bold 18px ${FONT}`;
                    ctx.fillStyle = ACCENT_TEXT_COLOR;
                    ctx.fillText(`S${record.season}`, PADDING + 20, y + 40);

                    // Draw challenge badge if present
                    if (record.challenge) {
                        const challengeLabel = getChallengeLabel(record.challenge.type);
                        ctx.font = `bold 12px ${FONT}`;
                        ctx.fillStyle = '#FFD700'; // Gold color for challenge badge
                        const badgeX = CANVAS_WIDTH - PADDING - 120;
                        const badgeY = y + 30;
                        ctx.fillText(challengeLabel, badgeX, badgeY);
                    }

                    ctx.font = `14px ${FONT}`;
                    ctx.fillStyle = TEXT_COLOR;
                    ctx.textAlign = 'right';
                    ctx.fillText(record.leagueName, CANVAS_WIDTH - PADDING - 20, y + 40);

                    ctx.strokeStyle = BORDER_COLOR;
                    ctx.beginPath();
                    ctx.moveTo(PADDING + 20, y + 60);
                    ctx.lineTo(CANVAS_WIDTH - PADDING - 20, y + 60);
                    ctx.stroke();

                    ctx.font = `14px ${FONT}`;
                    ctx.textAlign = 'left';

                    const goalDiff = record.goalsFor - record.goalsAgainst;
                    const goalDiffStr = goalDiff >= 0 ? `+${goalDiff}` : goalDiff;
                    const leagueTeamsCount = gameState.leagues[record.leagueName]?.teams.length || 20;
                    const statsCol1 = [
                        { label: 'Position:', value: getPositionDisplay(record, leagueTeamsCount, gameState.config) },
                        { label: 'Record:', value: `${record.wins}-${record.draws}-${record.losses}` },
                        { label: 'Goals:', value: `${record.goalsFor}-${record.goalsAgainst} (GD:${goalDiffStr})` },
                    ];
                    
                    const netSpend = record.income - record.spent;
                    const statsCol2 = [
                         { label: 'Spent:', value: `${formatValue(record.spent)}`, color: NEGATIVE_COLOR },
                         { label: 'Income:', value: `${formatValue(record.income)}`, color: POSITIVE_COLOR },
                         { label: 'Net:', value: netSpend >= 0 ? `+${formatValue(netSpend)}` : `-${formatValue(Math.abs(netSpend))}`, color: netSpend >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR },
                    ];

                    statsCol1.forEach((stat, i) => {
                        const statY = y + 90 + (i * 30);
                        ctx.fillStyle = TEXT_COLOR;
                        ctx.fillText(stat.label, PADDING + 30, statY);
                        ctx.fillStyle = TEXT_COLOR;
                        ctx.fillText(stat.value, PADDING + 200, statY);
                    });

                    statsCol2.forEach((stat, i) => {
                        const statY = y + 90 + (i * 30);
                        ctx.fillStyle = TEXT_COLOR;
                        ctx.fillText(stat.label, CANVAS_WIDTH / 2 + 30, statY);
                        ctx.fillStyle = stat.color || TEXT_COLOR;
                        ctx.fillText(stat.value, CANVAS_WIDTH / 2 + 150, statY);
                    });

                    currentY += CARD_HEIGHT + GAP;
                });
            }

            const urlY = canvas.height - PADDING / 2;

            if (cheatsUsed.length > 0) {
                const cheatText = `*This career used debug tools: ${cheatsUsed.join(', ')}.`;
                ctx.textAlign = 'center';
                ctx.font = `italic 14px ${FONT}`;
                ctx.fillStyle = NEGATIVE_COLOR;
                ctx.fillText(cheatText, CANVAS_WIDTH / 2, urlY - 20);
            }

            ctx.textAlign = 'center';
            ctx.font = `12px ${FONT}`;
            ctx.fillStyle = TEXT_COLOR;
            ctx.globalAlpha = 0.8;
            ctx.fillText('play at pixelsoccermanager.com', CANVAS_WIDTH / 2, urlY);
            ctx.globalAlpha = 1.0;

            const link = document.createElement('a');
            link.download = 'pixel-soccer-manager-career.png';
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Error generating career image:', err);
        } finally {
            setIsSharing(false);
        }
    };
    
    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1><FontAwesomeIcon icon={faBriefcase} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Manager</h1>
                <button
                    className="button"
                    onClick={() => setIsHelpOpen(true)}
                    style={{ padding: '8px 12px', fontSize: '0.9em' }}
                    title="Help"
                >
                    Help
                </button>
            </div>
            <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                Track your managerial career, board confidence, and club records across multiple seasons.
            </p>

            <div className="tabs">
                <button className={`tab-button ${activeTab === 'career' ? 'active' : ''}`} onClick={() => setActiveTab('career')}>Career History</button>
                <button className={`tab-button ${activeTab === 'board' ? 'active' : ''}`} onClick={() => setActiveTab('board')}>Board</button>
                <button className={`tab-button ${activeTab === 'records' ? 'active' : ''}`} onClick={() => setActiveTab('records')}>Club Records</button>
            </div>
            
            {adsEnabled && <AdPlaceholder />}

            {activeTab === 'board' && <BoardView team={playerTeam} />}

            {activeTab === 'career' && (
                <div className="panel panel-no-border-margin">
                    <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="manager-name-editor">
                            {isEditingName ? (
                                <input
                                    ref={nameInputRef}
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    onBlur={handleNameSave}
                                    onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
                                />
                            ) : (
                                <>
                                    <span>{gameState.managerName || 'Manager'}'s Career History</span>
                                    <button className="edit-name-btn" title="Edit Name" onClick={() => setIsEditingName(true)}>✏️</button>
                                </>
                            )}
                        </div>
                        <button className="button small" onClick={handleShare} disabled={isSharing}>
                            {isSharing ? 'Generating...' : 'Share Career 📸'}
                        </button>
                    </div>
                    {gameState.debugFlags?.aiPlayerUsed && (
                        <div className="debug-warning">
                            🤖 This career has used the AI Player debug tool.
                        </div>
                    )}
                    <div className="panel-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {reversedHistory.map((record, index) => (
                            <SeasonCard
                                key={`${record.season}-${record.teamName}`}
                                record={record}
                                isCurrent={index === 0}
                                leagueTeamsCount={gameState.leagues[record.leagueName]?.teams.length || 20}
                                config={gameState.config}
                                gameState={gameState}
                            />
                        ))}
                    </div>
                </div>
            )}

            {activeTab === 'records' && <ClubRecordsView clubHistory={playerTeam.clubHistory} />}

            <ManagerHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
        </>
    );
};